import React from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import classnames from 'classnames';
import SelfServeBuilderToolbox from 'SelfServeBuilderToolbox';
import Spinner from 'Spinner';
import FooterToggle from 'FooterToggle';
import SelfServeBuilderPostSharePanel from 'SelfServeBuilderPostSharePanel';
import SelfServeTooltipTour from 'SelfServeTooltipTour';
import { ZeroStateModal } from 'SelfServeModals';
import * as queryString from 'qs';
import ModalTypeEnum from 'Modals/ModalConstants';
import SelfServeBuilderCanvas from './SelfServeBuilderCanvas';

const BUILDER_EDITOR_LOADING_HEIGHT = 600;
const SAMPLE_DOCUMENT_HASH = 'sample-document';

class SelfServeBuilderEditor extends React.Component {
  static disableEditorInteraction(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  static renderLoadingComponent() {
    return (
      <React.Fragment>
        <div
          className="self-serve-builder-editor-wrapper"
          style={{
            height: BUILDER_EDITOR_LOADING_HEIGHT,
          }}
        />
        <Spinner />
      </React.Fragment>
    );
  }

  constructor(props) {
    super(props);
    this.state = {
      height: window.innerHeight,
      width: window.innerWidth,
      canvasScrollTop: 0,
      isSampleState: window.location.hash.substring(1) === SAMPLE_DOCUMENT_HASH,
    };

    this.resizeTimeout = false;
    this.editorRef = React.createRef();
    this.disableBuilder = this.disableBuilder.bind(this);
    this.renderEditorComponents = this.renderEditorComponents.bind(this);
  }

  componentDidMount() {
    const { documentUrl, documentType, isPreviewMode, isRecipientBuilder } = this.props;
    const queryParams = queryString.parse(window.location.search, { ignoreQueryPrefix: true });
    const { toolType } = queryParams;

    // Open zero state
    if ((!documentType && !isRecipientBuilder) || this.props.isNewAccountTemplatePage) {
      this.props.setZeroState();
      this.disableBuilder();
      return;
    }
    if (!isPreviewMode) {
      this.props.setPagesFromFile(documentUrl);
      this.props.loadCustomTemplateData(documentType, false, this.props.forceDownload, toolType);
      if (this.state.isSampleState) {
        this.props.setSampleState();
      }
    } else {
      this.disableBuilder();
      // While in preview mode, component needs to mount before we can grab the correct container
      // dimensions from editorRef to calculate the pdf scale correctly. This won't happen while
      // not in preview mode since the loading component will mount first, populating editorRef.
      this.setState({
        isMounted: true,
      });
    }

    if (!this.props.isRecipientBuilder) {
      window._track('Textbox | Onboarding Tooltip', 'Template Builder');
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.documentType && !prevProps.documentType) {
      this.props.setPagesFromFile(this.props.documentUrl);
    }
  }

  // Catches and disables all click and mousedown events on editor
  disableBuilder() {
    if (!this.editorRef.current) {
      return;
    }
    this.editorRef.current.addEventListener('click', SelfServeBuilderEditor.disableEditorInteraction, true);
    this.editorRef.current.addEventListener('mousedown', SelfServeBuilderEditor.disableEditorInteraction, true);
  }

  renderEditorComponents() {
    const {
      isPreviewMode,
      isShared,
      publishedAt,
      documentType,
      isRecipientBuilder,
    } = this.props;
    // While in preview mode, only render main editor components if
    // editorRef is defined and the component has been mounted.
    // Without the editor ref the scale prop will not have been calculated correctly
    if (isPreviewMode && !this.state.isMounted) return null;
    const isOverlayVisible = (isShared || publishedAt) && !isPreviewMode;
    const isZeroState = !documentType && !isRecipientBuilder;
    return (
      <React.Fragment>
        {!isPreviewMode &&
          <SelfServeBuilderToolbox
            isSampleState={this.state.isSampleState}
            isZeroState={isZeroState}
          />
        }
        {!isPreviewMode && !this.props.isZeroStateModalOpen &&
          <SelfServeTooltipTour canvasScrollTop={this.state.canvasScrollTop} />
        }
        {(this.props.isShared || this.props.publishedAt) && !isPreviewMode &&
          <SelfServeBuilderPostSharePanel
            documentType={this.props.documentType}
          />
        }

        <SelfServeBuilderCanvas
          editorRef={this.editorRef}
          isLoading={this.props.isLoading}
          isPreviewMode={isPreviewMode}
          isScalingEnabled={this.props.isScalingEnabled}
          isOverlayVisible={!!isOverlayVisible}
          openPanel={this.props.openPanel}
          pages={this.props.pages}
          popupGroups={this.props.popupGroups}
          popupGroupsOnPage={this.props.popupGroupsOnPage}
          popupIdsOnPage={this.props.popupIdsOnPage}
          popups={this.props.popups}
          setPagesFromFile={this.props.setPagesFromFile}
        />
      </React.Fragment>
    );
  }

  render() {
    const {
      documentType,
      isLoading,
      isNewAccountTemplatePage,
      isPreviewMode,
      isRecipientBuilder,
    } = this.props;

    const isZeroState = !documentType && !isRecipientBuilder;
    const builderWrapperClassNames = classnames(
      'self-serve-builder-wrapper',
      {
        'recipient-builder-loading-wrapper': isPreviewMode && isLoading,
        'zero-state-wrapper': isZeroState || isNewAccountTemplatePage,
      },
    );

    return (
      <React.Fragment>
        <div className="self-serve-builder-container">
          <ZeroStateModal
            activeModal={
              this.props.isZeroStateModalOpen
              ? ModalTypeEnum.SELF_SERVE_ZERO_STATE
              : ''
            }
            isOpen={this.props.isZeroStateModalOpen}
            isNewAccountModal={this.props.isNewAccountTemplatePage}
          />
          <div
            ref={this.editorRef}
            className={builderWrapperClassNames}
          >
            {isLoading && !isPreviewMode ? (
               SelfServeBuilderEditor.renderLoadingComponent()
            ) : (
               this.renderEditorComponents()
            )}
          </div>
          <FooterToggle />
        </div>
      </React.Fragment>
    );
  }
}

SelfServeBuilderEditor.propTypes = {
  documentType: PropTypes.string.isRequired,
  documentUrl: PropTypes.string.isRequired,
  forceDownload: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isNewAccountTemplatePage: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  isRecipientBuilder: PropTypes.bool,
  isScalingEnabled: PropTypes.bool,
  isShared: PropTypes.bool.isRequired,
  isZeroStateModalOpen: PropTypes.bool,
  loadCustomTemplateData: PropTypes.func,
  openPanel: PropTypes.func,
  pages: PropTypes.arrayOf(PropTypes.shape()),
  popupGroups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      pageIndex: PropTypes.number,
      fieldIds: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  popupGroupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
  popupIdsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
  popups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
  publishedAt: PropTypes.string,
  setPagesFromFile: PropTypes.func.isRequired,
  setSampleState: PropTypes.func,
  setZeroState: PropTypes.func,
};

SelfServeBuilderEditor.defaultProps = {
  forceDownload: false,
  isNewAccountTemplatePage: false,
  isPreviewMode: false,
  isRecipientBuilder: false,
  isScalingEnabled: false,
  isZeroStateModalOpen: false,
  loadCustomTemplateData: () => null,
  openPanel: () => null,
  pages: [],
  publishedAt: '',
  setSampleState: () => null,
  setZeroState: () => null,
};

export default SelfServeBuilderEditor;



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderEditor.jsx